import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Alert } from 'reactstrap';
import { AccentDialog } from '../../dialogs/AccentDialog';
import { showDialog, accentUtils, accentUserLang } from '../../services/HelperService';
import { queryNoTracking } from '../../services/DataService';
import { AccentSpinner } from '../AccentSpinner';
import { AccentButton } from '../AccentButton';
import { AccentCheckUnbound } from '../unbound/AccentCheckUnbound';
import { AccentDateTimeUnbound } from '../unbound/AccentDateTimeUnbound';
import { FilterButtons } from '../FilterButtons';
import { withPosition } from '../AccentDisplay';
import { getUser } from '../../services/UserService';

const query_GetJobChangesSummaryReport = queryNoTracking("GetJobChangesSummaryReport", undefined, true);
const query_GetJobChangesSummaryUsers = queryNoTracking("GetJobChangesSummaryUsers");
const query_GetJobChangesSummaryDefaultStart = queryNoTracking("GetJobChangesSummaryDefaultStart");

export const showJobChangesSummaryReport = ({ jobId, primaryUserInitials } = {}) => {
    if (!jobId) return;
    showDialog(<JobChangesSummaryReportDlg jobId={jobId} primaryUserInitials={primaryUserInitials} />);
};

const JobChangesSummaryReportDlgBase = ({ onClose, jobId, primaryUserInitials, position, posID }) => {
    const now = useMemo(() => new Date(), []);
    const defaultStart = useMemo(() => new Date(now.getTime() - 24 * 60 * 60 * 1000), [now]);

    // Persist non-date settings per user
    const storageKey = useMemo(() => {
        const u = getUser();
        const uid = u?.ID ?? "anon";
        return `JobChangesSummaryReportDlg.settings.${uid}`;
    }, []);

    // Load saved settings synchronously for initial render, because FilterButtons/AccentCheckUnbound are uncontrolled
    // (they only use defaultValue on mount).
    const savedSettings = useMemo(() => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (!raw) return null;
            const s = JSON.parse(raw);
            return (s && s.v === 1) ? s : null;
        } catch {
            return null;
        }
    }, [storageKey]);

    const startTouchedRef = useRef(false);
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(null); // optional

    // Settings (restore from localStorage at mount)
    const [groupMode, setGroupMode] = useState(() => savedSettings?.groupMode ?? "Product"); // matches enum name
    const [toggleFlags, setToggleFlags] = useState(() =>
        Array.isArray(savedSettings?.toggleFlags) ? savedSettings.toggleFlags : ["userChangedOnly"]
    ); // default ON
    // Do not persist/restore the user filter; always default to "not set" when dialog opens.
    const [selectedUsers, setSelectedUsers] = useState([]);

    const [availableUsers, setAvailableUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    const [html, setHtml] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const iframeRef = useRef(null);

    const onStartChange = (d) => {
        startTouchedRef.current = true;
        setStartDate(d);
    };

    const startIso = useMemo(() => (startDate ? startDate.toISOString() : null), [startDate]);
    const endIso = useMemo(() => (endDate ? endDate.toISOString() : null), [endDate]);

    // Persist non-date settings whenever they change
    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify({
                v: 1,
                groupMode,
                toggleFlags
            }));
        } catch {
            // ignore quota / disabled storage
        }
    }, [storageKey, groupMode, toggleFlags]);

    const [defaultStartLoaded, setDefaultStartLoaded] = useState(false);
    const autoRanRef = useRef(false);

    // Default start = last edit session start for this Job (fallback to last 24h)
    useEffect(() => {
        let cancelled = false;

        const loadDefaultStart = async () => {
            try {
                const r = await query_GetJobChangesSummaryDefaultStart.getFirstOrDefault({ jobId });
                if (cancelled) return;

                const raw = r?.Start;
                if (!raw) return;

                const d = new Date(raw);
                if (Number.isNaN(d.getTime())) return;

                // Only apply if user hasn't manually changed the start field yet
                if (!startTouchedRef.current) {
                    setStartDate(d);
                }
            } catch (e) {
                // non-fatal
            } finally {
                if (!cancelled) setDefaultStartLoaded(true);
            }
        };

        loadDefaultStart();

        return () => {
            cancelled = true;
        };
    }, [jobId]);

    const loadUsers = async () => {
        if (!startIso) return;
        setLoadingUsers(true);
        try {
            const res = await query_GetJobChangesSummaryUsers.getAll({ jobId, start: startIso, end: endIso });
            const list = (res || []).map(x => ({ ID: x.ID, Text: x.Text }));
            setAvailableUsers(list);
        } catch (e) {
            // non-fatal
        } finally {
            setLoadingUsers(false);
        }
    };

    useEffect(() => {
        loadUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, startIso, endIso]);

    const runReport = async () => {
        setError(null);
        setLoading(true);
        try {
            const flags = new Set(toggleFlags);
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const locale = accentUserLang || navigator.language || "en-AU";

            const res = await query_GetJobChangesSummaryReport.getAll({
                jobId,
                start: startIso,
                end: accentUtils.isEmpty(endIso) ? null : endIso,
                groupMode,
                splitColumns: flags.has("splitColumns"),
                combineMultiCompositeProducts: flags.has("combineMulti"),
                userChangedOnly: flags.has("userChangedOnly"),
                showImages: flags.has("showImages"),
                showInstallationNotes: flags.has("showInstallNotes"),
                primaryUserInitials: accentUtils.isEmpty(primaryUserInitials) ? null : primaryUserInitials,
                userInitialsFilter: selectedUsers?.length ? selectedUsers : null,
                timeZone,
                locale
            });

            setHtml(res || "");
            if (!res || res.length === 0) {
                setError("No data returned for the selected period/filters.");
            }
        } catch (e) {
            setError(e?.message || "Failed to generate report.");
        } finally {
            setLoading(false);
        }
    };

    // Auto-run once after default start + localStorage settings have been applied
    useEffect(() => {
        if (autoRanRef.current) return;
        if (!defaultStartLoaded) return;
        if (!startIso) return;

        autoRanRef.current = true;
        runReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultStartLoaded, startIso]);

    const openInNewWindow = () => {
        // Prefer the iframe’s current rendered DOM (most reliable / no rerun)
        const doc = iframeRef.current?.contentDocument;
        const htmlStr = doc?.documentElement?.outerHTML || html;
        if (accentUtils.isEmpty(htmlStr)) return;

        const blob = new Blob([`<!doctype html>${htmlStr}`], { type: "text/html" });
        const url = URL.createObjectURL(blob);

        window.open(url, "_blank", "noopener,noreferrer");

        // cleanup
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
    };

    const groupModeOptions = useMemo(() => ([
        { ID: "Product", Text: "Group by Product" },
        { ID: "Line", Text: "By Line View" },
    ]), []);

    const userButtons = useMemo(() => (availableUsers || []).map(u => ({
        ID: u.ID,
        Text: u.Text,        
    })), [availableUsers]);

    // Fill down to the bottom of the dialog content area
    const reportHeight = Math.max(260, (position?.toScreenHeight ?? 600) - 16);

    return (
        <AccentDialog
            headerContent="Changes Summary Report"
            onClose={onClose}
            relativeWidth={d => d.width - 80}
            relativeHeight={d => d.height - 80}
            actionsContent={[
                <AccentButton key="run" onClick={runReport} disabled={loading || !startIso}>Run / Refresh</AccentButton>,
                <AccentButton key="open" onClick={openInNewWindow} disabled={loading || accentUtils.isEmpty(html)}>Open in New Window</AccentButton>,
                <AccentButton key="close" onClick={() => onClose({})}>Close</AccentButton>
            ]}
        >
            <Row className="mb-2">
                <Col md={4}>
                    <AccentDateTimeUnbound
                        key={`start-${startDate ? startDate.getTime() : "null"}`}
                        label="Start"
                        required
                        defaultValue={startDate}
                        disabled={true}
                        onChange={onStartChange}
                    />
                </Col>
                <Col md={4}>
                    <AccentDateTimeUnbound
                        key={`end-${endDate ? endDate.getTime() : "null"}`}
                        label="End (optional)"
                        defaultValue={endDate}
                        disabled={true}
                        onChange={setEndDate}
                    />
                </Col>
            </Row>

            <Row className="mb-2">
                <Col md={12}>
                    <div className="d-flex" >
                        <div >
                            <FilterButtons
                                label="Group Mode"
                                options={groupModeOptions}
                                check={false}
                                defaultValue={groupMode}
                                onChange={(e) => setGroupMode(e.value)}
                            />
                        </div>
                        <div style={{ paddingLeft: 16 }}>
                            {userButtons.length === 0 && !loadingUsers ? (
                                <div className="text-muted" style={{ fontSize: 12 }}>No users found in period (or none recorded).</div>
                            ) : (
                                <FilterButtons
                                    label="User Filter (Initials)"
                                    options={userButtons}
                                    check={true}
                                    defaultValue={selectedUsers}
                                    onChange={(e) => setSelectedUsers(e.value)}
                                />
                            )}
                        </div>
                    </div>
                </Col>
            </Row>

            <Row className="mb-2">
                <Col md={12} className="d-flex flex-wrap" style={{ gap: 16 }}>
                    <AccentCheckUnbound
                        tagName="userChangedOnly"
                        defaultValue={(toggleFlags || []).includes("userChangedOnly")}
                        label="Hide Configuration Options Not Directly Set by Users"
                        onChange={(e) => setToggleFlags(prev => e.checked
                            ? Array.from(new Set([...(prev || []), "userChangedOnly"]))
                            : (prev || []).filter(x => x !== "userChangedOnly")
                        )}
                    />
                    <AccentCheckUnbound
                        tagName="splitColumns"
                        defaultValue={(toggleFlags || []).includes("splitColumns")}
                        label="Split Columns"
                        onChange={(e) => setToggleFlags(prev => e.checked
                            ? Array.from(new Set([...(prev || []), "splitColumns"]))
                            : (prev || []).filter(x => x !== "splitColumns")
                        )}
                    />
                    <AccentCheckUnbound
                        tagName="showImages"
                        defaultValue={(toggleFlags || []).includes("showImages")}
                        label="Images"
                        onChange={(e) => setToggleFlags(prev => e.checked
                            ? Array.from(new Set([...(prev || []), "showImages"]))
                            : (prev || []).filter(x => x !== "showImages")
                        )}
                    />
                    <AccentCheckUnbound
                        tagName="showInstallNotes"
                        defaultValue={(toggleFlags || []).includes("showInstallNotes")}
                        label="Installation Notes"
                        onChange={(e) => setToggleFlags(prev => e.checked
                            ? Array.from(new Set([...(prev || []), "showInstallNotes"]))
                            : (prev || []).filter(x => x !== "showInstallNotes")
                        )}
                    />
                    <AccentCheckUnbound
                        tagName="combineMulti"
                        defaultValue={(toggleFlags || []).includes("combineMulti")}
                        label="Combine Multi-Composite"
                        onChange={(e) => setToggleFlags(prev => e.checked
                            ? Array.from(new Set([...(prev || []), "combineMulti"]))
                            : (prev || []).filter(x => x !== "combineMulti")
                        )}
                    />
                </Col>
            </Row>

            {error && <Alert color="warning">{error}</Alert>}

            {loading ? (
                <div className="text-center p-4">
                    <AccentSpinner />
                    <div className="mt-2">Generating report…</div>
                </div>
            ) : (
                <div id={posID} style={{ border: "1px solid #e5e7eb", borderRadius: 6, overflow: "auto" }}>
                    <iframe
                        ref={iframeRef}
                        title="Changes Summary Report"
                        srcDoc={html || "<div style='padding:12px;color:#6b7280;'>Run the report to view results.</div>"}
                        style={{ width: "100%", height: reportHeight, border: "0" }}
                    />
                </div>
            )}
        </AccentDialog>
    );
};

const JobChangesSummaryReportDlg = withPosition(JobChangesSummaryReportDlgBase);


